import type { MailClient } from '../api/client';
import type { Identity } from '../api/types';

/*
    The one identity.

    Iris Mail fetched a list of them from its server and cached it, because a Stalwart account can
    hold several and the composer needed them before it could seed anything. Here there is exactly
    one — the mailbox that signed in — and the backend refuses to send as anything else, so there
    is nothing to choose between.

    The name and signature live IN THE ACCOUNT now (a message in a folder of its own, see
    `server/internal/mailbox/identity.go`), not in the browser. What is left in the browser is a
    cache: the composer paints with the last known signature and replaces it the moment the account
    answers, so opening a reply does not wait for a round trip.
*/

export function cachedIdentities(client: MailClient, accountId: string): Identity[] | null {
    const one = client.cachedIdentity(accountId);
    return one ? [one] : null;
}

/*
    For the composer, and only for the composer: a failed read falls back to the cache.

    The choice is different from the identity editor's on purpose. A reply that goes out without a
    signature is an annoyance; an EDITOR that shows an empty signature after a failed read invites
    the person to retype it over the real one. So the loss-bearing screen throws and this one
    shrugs — and both say so where they do it.
*/
export async function loadIdentities(client: MailClient, accountId: string): Promise<Identity[]> {
    try {
        return await client.identities(accountId);
    } catch {
        return cachedIdentities(client, accountId) ?? [{ id: accountId, email: accountId, name: '', htmlSignature: '', textSignature: '' }];
    }
}

/** Called after the identity editor writes. The write refreshes the cache itself. */
export function forgetIdentities(_accountId: string): void {}
